import { Star, ExternalLink } from "lucide-react";
import { GOOGLE_REVIEWS_LINK, waLink } from "../../lib/constants";
import useReveal from "../../hooks/useReveal";

const DEPOIMENTOS = [
  {
    name: "Marcela Silva",
    when: "há 1 mês",
    color: "#0D47A1",
    text:
      "Descobri o derrame pleural num exame de rotina e fiquei apavorada. O Dr. Mateus explicou tudo com muita calma, fez a toracocentese guiada e hoje estou completamente bem. Profissional atencioso e preciso.",
  },
  {
    name: "Ricardo Almeida",
    when: "há 2 meses",
    color: "#00897B",
    text:
      "Passei por uma pleuroscopia VATS para investigar um derrame recorrente. A recuperação foi muito rápida e o diagnóstico veio com segurança. Sou eternamente grato pela condução cuidadosa.",
  },
  {
    name: "Ana Rita",
    when: "há 3 semanas",
    color: "#6D4C41",
    text:
      "Não tenho do que reclamar, somente agradecer por todo cuidado e atenção dedicados a mim não somente no momento cirúrgico, mas principalmente durante todo o pós operatório! Profissional de excelência e equipe maravilhosa!",
  },
  {
    name: "Gabriel",
    when: "há 3 semanas",
    color: "#795548",
    text:
      "Excelente profissional, humano e competente. Recomendo sem hesitar. Esclareceu todas as minhas dúvidas sobre o derrame pleural.",
  },
  {
    name: "Juliana Mendes",
    when: "há 2 meses",
    color: "#5E35B1",
    text:
      "Minha mãe teve derrame por insuficiência cardíaca. O Dr. Mateus conduziu com a cardiologista e ela hoje está estável, respirando bem. Atendimento humanizado do começo ao fim.",
  },
];

function GoogleLogo({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

export default function Depoimentos() {
  const { ref, isVisible } = useReveal();

  return (
    <section
      id="depoimentos"
      ref={ref}
      className={`py-24 md:py-32 bg-white reveal-section ${isVisible ? "is-visible" : ""}`}
      data-testid="depoimentos-section"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center bg-[#0D47A1]/10 text-[#0D47A1] rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            Depoimentos
          </div>
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-[#212121] mb-4 leading-tight">
            Pacientes que Passaram por Isso <br />
            Contam Sua História
          </h2>
          <p className="text-[#212121]/65 text-base">
            Histórias reais de quem descobriu derrame pleural e agora respira tranquilo.
          </p>
        </div>

        <div
          className="flex gap-4 overflow-x-auto pb-4 mb-8 no-scrollbar"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {DEPOIMENTOS.map((d, idx) => (
            <div
              key={idx}
              data-testid={`google-review-card-${idx + 1}`}
              className="flex-shrink-0 w-72 bg-white border border-gray-100 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow"
              style={{ scrollSnapAlign: "start" }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-white font-poppins font-bold text-sm flex-shrink-0"
                    style={{ backgroundColor: d.color }}
                  >
                    {d.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-poppins font-semibold text-[#212121] text-sm leading-tight">
                      {d.name}
                    </div>
                    <div className="text-[#212121]/45 text-xs">{d.when}</div>
                  </div>
                </div>
                <GoogleLogo />
              </div>
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className="text-[#FBBC05] fill-[#FBBC05]" />
                ))}
              </div>
              <p className="text-[#212121]/70 text-sm leading-relaxed mt-3">"{d.text}"</p>
            </div>
          ))}

          <a
            href={GOOGLE_REVIEWS_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-more-reviews-card"
            className="flex-shrink-0 w-64 border-2 border-dashed border-[#0D47A1]/30 rounded-2xl p-5 flex flex-col items-center justify-center gap-3 hover:border-[#0D47A1] hover:bg-[#0D47A1]/5 transition-all group"
            style={{ scrollSnapAlign: "start" }}
          >
            <GoogleLogo />
            <span className="font-poppins font-semibold text-[#0D47A1] text-sm text-center group-hover:underline">
              Ver todas as avaliações no Google
            </span>
            <ExternalLink size={16} className="text-[#0D47A1]/60" />
          </a>
        </div>

        <div className="mt-8 bg-white border border-gray-100 rounded-3xl p-6 md:p-8 shadow-sm max-w-4xl mx-auto mb-10">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
            <div className="flex items-center gap-4 flex-shrink-0">
              <GoogleLogo size={44} />
              <div className="text-left">
                <div className="flex items-center gap-1">
                  <span className="font-poppins font-bold text-3xl text-[#212121]">5.0</span>
                  <div className="flex gap-0.5 ml-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={16} className="text-[#FBBC05] fill-[#FBBC05]" />
                    ))}
                  </div>
                </div>
                <div className="text-[#212121]/60 text-xs font-medium">Avaliações no Google</div>
              </div>
            </div>
            <div className="hidden md:block w-px h-12 bg-gray-200" />
            <div className="flex-1 text-center md:text-left">
              <p className="text-[#212121]/75 text-sm md:text-base leading-relaxed">
                Leia avaliações reais de pacientes no perfil oficial do Google Meu Negócio.
              </p>
            </div>
            <a
              href={GOOGLE_REVIEWS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-business-cta"
              className="inline-flex items-center gap-2 bg-white border-2 border-[#0D47A1] text-[#0D47A1] font-poppins font-bold text-sm px-6 py-3.5 rounded-full hover:bg-[#0D47A1] hover:text-white transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 flex-shrink-0"
            >
              <GoogleLogo size={18} />
              Ver no Google Meu Negócio
              <ExternalLink size={14} />
            </a>
          </div>
        </div>

        <div className="text-center">
          <p className="text-[#212121]/60 text-sm mb-4">Transforme também a sua história.</p>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="depoimentos-cta-button"
            className="inline-flex items-center gap-2 bg-[#0D47A1] text-white font-poppins font-bold px-10 py-4 rounded-full hover:bg-[#00897B] transition-colors duration-200 shadow-lg hover:-translate-y-0.5"
          >
            AGENDAR CONSULTA COM CIRURGIÃO TORÁCICO
          </a>
        </div>
      </div>
    </section>
  );
}
